/**
 * Vertical Gallery Modal
 * -----------------------------------------------------------------------------
 * Replaces the PhotoSwipe lightbox with a simple vertical-scroll image viewer.
 *
 * HOW IT WORKS
 * The theme's <product-gallery> element dispatches a "lightbox:open" custom
 * event (bubbles: true) whenever the zoom button / image / pinch gesture is
 * triggered. It normally listens for this itself and opens PhotoSwipe.
 *
 * Here we add our own listener on `document` in the CAPTURE phase. Capture
 * listeners on ancestors always run BEFORE the event reaches the target, so
 * we can call stopPropagation() to prevent PhotoSwipe from ever being
 * initialized, and open our own modal instead.
 *
 * INSTALLATION
 * 1. Upload this file to your theme as: assets/vertical-gallery-modal.js
 * 2. Add this tag once, right before </body> in layout/theme.liquid:
 *      <script src="{{ 'vertical-gallery-modal.js' | asset_url }}" defer></script>
 *    (Make sure it loads AFTER assets/theme.js — "defer" scripts run in
 *    document order, so just place it after the theme.js tag.)
 * 3. That's it — no other files need to change. You can leave "allow-zoom"
 *    on the product-gallery in the liquid file; PhotoSwipe itself is simply
 *    never imported/initialized anymore because we intercept the event
 *    before ProductGallery's own "lightbox:open" handler runs.
 * -----------------------------------------------------------------------------
 */
(function () {
  "use strict";

  var STYLE_ID = "vertical-gallery-modal-styles";

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = [
      ".vertical-gallery-modal{position:fixed;inset:0;z-index:1000;background:#fff;display:flex;flex-direction:column;align-items:center;overflow:hidden;}",
      ".vertical-gallery-modal__close{position:fixed;top:24px;right:16px;width:max-content;height:auto;border:none;color:#000;font-size:13px;line-height:1;cursor:pointer;z-index:1001;display:flex;align-items:center;justify-content:center;}",
      ".vertical-gallery-modal__close:hover{background:rgba(255,255,255,.24);}",
      ".vertical-gallery-modal__scroll{width:100%;height:100%;overflow-y:auto;overflow-x:hidden;-webkit-overflow-scrolling:touch;padding:56px 0 0 0;box-sizing:border-box;scroll-behavior:smooth;}",
      ".vertical-gallery-modal__scroll img{display:block;width:100%;max-width:720px;height:auto;margin:0 auto 8px auto;}",
      ".vertical-gallery-modal__scroll img:last-child{margin-bottom: 0;",
      "html.lock{overflow:hidden;}"
    ].join("");
    document.head.appendChild(style);
  }

  function collectGalleryImages(galleryEl) {
    var carousel = galleryEl.querySelector(".product-gallery__carousel");
    if (!carousel) return [];
    var mediaEls = Array.prototype.slice.call(
      carousel.querySelectorAll('[data-media-type="image"]')
    );
    return mediaEls
      .map(function (mediaEl) {
        return mediaEl.querySelector("img");
      })
      .filter(Boolean);
  }

  function openVerticalGalleryModal(galleryEl, startIndex) {
    var sourceImages = collectGalleryImages(galleryEl);
    if (!sourceImages.length) return;

    injectStyles();

    var overlay = document.createElement("div");
    overlay.className = "vertical-gallery-modal";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");

    var closeButton = document.createElement("button");
    closeButton.type = "button";
    closeButton.className = "vertical-gallery-modal__close";
    closeButton.setAttribute("aria-label", "Close");
    closeButton.textContent = "Close X";

    var scrollArea = document.createElement("div");
    scrollArea.className = "vertical-gallery-modal__scroll";

    sourceImages.forEach(function (img) {
      var clone = document.createElement("img");
      clone.src = img.currentSrc || img.src;
      if (img.srcset) clone.srcset = img.srcset;
      clone.alt = img.alt || "";
      clone.loading = "lazy";
      scrollArea.appendChild(clone);
    });

    overlay.appendChild(closeButton);
    overlay.appendChild(scrollArea);
    document.body.appendChild(overlay);
    document.documentElement.classList.add("lock");

    function close() {
      overlay.remove();
      document.documentElement.classList.remove("lock");
      document.removeEventListener("keydown", onKeydown);
    }

    function onKeydown(event) {
      if (event.key === "Escape") close();
    }

    closeButton.addEventListener("click", close);
    document.addEventListener("keydown", onKeydown);

    if (typeof startIndex === "number" && scrollArea.children[startIndex]) {
      // Wait a frame so layout is ready before scrolling
      requestAnimationFrame(function () {
        scrollArea.children[startIndex].scrollIntoView({ block: "start" });
      });
    }
  }

  document.addEventListener(
    "lightbox:open",
    function (event) {
      var galleryEl = event.target.closest
        ? event.target.closest("product-gallery")
        : null;
      if (!galleryEl) return;

      // Stop PhotoSwipe (ProductGallery's own handler) from ever running
      event.stopPropagation();
      event.preventDefault();

      var index =
        event.detail && typeof event.detail.index === "number"
          ? event.detail.index
          : undefined;

      openVerticalGalleryModal(galleryEl, index);
    },
    true // capture phase — runs before the target's own listener
  );
})();
